import React, { useMemo } from "react";
import useRefCallback from "use-ref-callback";
import styled from "styled-components";
import TabsNew, { TTabsItem } from "@atlas/uicorn/lib/Display/TabsNew";
import { TOptionsListItem } from "@atlas/uicorn/lib/Control/OptionsList";
import { AppSettings } from "../../../lib/get-app-settings";
import LoginAlert from "./login-alert";
import getBaseUrl from "../../../lib/get-base-url";
import HeaderActions from "../../../components/customer-portal/conversations-list/header-actions";
import ConversationsList from "../../../components/customer-portal/conversations-list/conversations-list";
import { useRouter } from "next/router";
import useCustomerPortalContext, {
  ConversationTab,
} from "../customer-portal-context";
import t from "../../../texts/t";

const ConversationsTabs = function ConversationsTabs(
  props: TConversationsTabsProps
) {
  const { className, appSettings, hideTabs } = props;

  const baseUrl = useMemo(() => getBaseUrl(appSettings), [appSettings]);

  const router = useRouter();
  const { customer, totals } = useCustomerPortalContext();

  const tabs: TTabsItem[] = useMemo(
    () => [
      {
        key: ConversationTab.customer,
        label: t("My Tickets", appSettings.language),
        description: totals[ConversationTab.customer].toString(),
      },
      ...(appSettings?.enableAccountLevelSupportPortal
        ? [
            {
              key: ConversationTab.account,
              label: t("Company tickets", appSettings.language),
              description: totals[ConversationTab.account].toString(),
            },
          ]
        : []),
    ],
    [totals, appSettings]
  );

  const selectedTab = useMemo(
    () =>
      (router.query?.tab?.toString() ||
        ConversationTab.customer) as ConversationTab,
    [router.query]
  );

  const handleTabSelect = useRefCallback(({ key: tab }: TOptionsListItem) => {
    const { companySlug, ...oldQuery } = router.query;

    router.replace({
      pathname: `${baseUrl}/customer-portal`,
      query: { ...oldQuery, tab },
    });
  });

  const isVisitor = useMemo(() => !customer || customer.isVisitor, [customer]);

  return (
    <SConversationTabs className={className}>
      {appSettings.supportPortalLoginUrl &&
        (!customer || customer.isVisitor) && (
          <LoginAlert
            language={appSettings.language}
            loginUrl={appSettings.supportPortalLoginUrl}
          />
        )}

      <STabsRow>
        {!hideTabs && (
          <STabs items={tabs} cursor={selectedTab} onChoose={handleTabSelect} />
        )}
        <SSpacer />
        <HeaderActions
          appSettings={appSettings}
          tab={selectedTab}
          language={appSettings.language}
          isVisitor={isVisitor}
        />
      </STabsRow>

      <ConversationsList
        tab={selectedTab}
        baseUrl={baseUrl}
        language={appSettings.language}
      />
    </SConversationTabs>
  );
};

export default ConversationsTabs;

const SSpacer = styled.div`
  flex: 1;
`;

const STabs = styled(TabsNew)`
  border-radius: 0;

  & .atlas-tabs-new-tab {
    padding: 20px 8px;
  }
`;

const STabsRow = styled.div`
  position: sticky;
  top: 0;
  z-index: 2;
  height: var(--tabs-row-height);
  background-color: ${(p) => p.theme.color.grey0};
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid ${(p) => p.theme.color.grey6};
`;

const SConversationTabs = styled.div`
  position: relative;
  z-index: 0;
`;

type TConversationsTabsProps = {
  className?: string;
  appSettings: AppSettings;
  hideTabs?: boolean;
};
